import React, { useMemo } from 'react';
import { Calendar } from '@demind-inc/core';
import { Select, MenuItem, FormControl, SelectChangeEvent } from '@mui/material';
import { mdiAccountCircleOutline, mdiCheck } from '@mdi/js';
import Icon from '@mdi/react';
import clsx from 'clsx';

import './CalendarAccountDropdown.scss';
import { useAuthContext } from '../../../../data-access';
import { isAppleCalendarDesktopSupported } from '../../../../helpers/appleCalendarDesktop.helpers';
import { useTranslation } from 'react-i18next';

interface CalendarAccountDropdownProps {
  calendars: Calendar[];
  selectedCalendarId: string;
  onSelectCalendarId: (calendarId: string) => void;
  className?: string;
  menuPaperClassName?: string;
  showUserIcon?: boolean;
  disabled?: boolean;
}

interface SelectedCalendarInfo {
  name: string;
  color: string;
}

const CalendarAccountDropdown: React.FC<CalendarAccountDropdownProps> = ({
  calendars,
  selectedCalendarId,
  onSelectCalendarId,
  className,
  menuPaperClassName,
  showUserIcon = false,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const isAppleDesktopSupported = isAppleCalendarDesktopSupported();

  const sortedCalendars = useMemo(() => {
    return calendars
      .filter(
        ({ visible, calendarType }) =>
          visible || (calendarType === 'apple' && isAppleDesktopSupported)
      )
      .sort((_, info) => (info.default ? 1 : -1))
      .sort((a) => (a.calendarType === 'google' ? 1 : 0))
      .sort((a) => (a.rootEmail === user.email ? 1 : 0));
  }, [calendars, isAppleDesktopSupported, user.email]);

  const handleChange = (event: SelectChangeEvent<string>) => {
    onSelectCalendarId(event.target.value);
  };

  const selectedCalendarInfo = useMemo<SelectedCalendarInfo | null>(() => {
    const selectedCalendar = calendars.find(
      (calendar) => calendar.calendarId === selectedCalendarId
    );

    if (!selectedCalendar) {
      return null;
    }

    if (selectedCalendar.calendarType === 'lifestack') {
      return {
        name: `${selectedCalendar.name} (Lifestack)`,
        color: selectedCalendar.color,
      };
    }

    return {
      name: selectedCalendar.name,
      color: selectedCalendar.color,
    };
  }, [calendars, selectedCalendarId]);

  const selectedCalendarName = selectedCalendarInfo?.name ?? t('calendar.eventMenu.selectCalendar');
  const selectedCalendarColor = selectedCalendarInfo?.color ?? 'transparent';

  return (
    <FormControl
      disabled={disabled}
      className={clsx(
        'calendar-account-dropdown__form-control',
        className,
        'calendar-account-dropdown__form-control--new-tabs'
      )}
    >
      <Select
        labelId="calendar-account-label"
        id="calendar-account-select"
        variant="standard"
        value={selectedCalendarId}
        onChange={handleChange}
        disabled={disabled}
        MenuProps={{
          PaperProps: {
            className: clsx('calendar-account-dropdown__menu-paper', menuPaperClassName),
          },
        }}
        className="calendar-account-dropdown__select"
        renderValue={() => (
          <div className="calendar-account-dropdown__selected-value">
            {showUserIcon && (
              <span className="calendar-account-dropdown__selected-value__icon-container">
                <Icon
                  path={mdiAccountCircleOutline}
                  size="20px"
                  className="calendar-account-dropdown__selected-value__user-icon"
                />
              </span>
            )}
            <div
              className="calendar-account-dropdown__selected-value__color-indicator"
              style={{ backgroundColor: selectedCalendarColor }}
            />

            <span
              className={clsx(
                'calendar-account-dropdown__selected-value__text',
                'calendar-account-dropdown__selected-value__text--new-tabs'
              )}
            >
              {selectedCalendarName}
            </span>
          </div>
        )}
      >
        {sortedCalendars.map((calendar) => (
          <MenuItem
            key={calendar.calendarId}
            value={calendar.calendarId}
            className="calendar-account-dropdown__menu-item"
          >
            <div className="calendar-account-dropdown__menu-item">
              <div
                className="calendar-account-dropdown__color-indicator"
                style={{ backgroundColor: calendar.color }}
              />
              <span className="calendar-account-dropdown__calendar-name">
                {calendar.name || calendar.internalGCalendarId}
                {calendar.calendarType === 'lifestack' && ' (Lifestack)'}
                {calendar.calendarType === 'outlook' && ` (${calendar.rootEmail})`}
              </span>
              {calendar.calendarId === selectedCalendarId && (
                <Icon path={mdiCheck} size="24px" />
              )}
            </div>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default CalendarAccountDropdown;
